/**
 * MINI_FIX_CS — Shared per-contract reporting figures for the audit-period
 * accrual window, paired in **native** (contract currency) and **functional**
 * (engagement reporting currency).
 *
 * Three surfaces consume this:
 *   • frontend/src/features/export/sheets/contractsSheet.ts  (6. Sözleşmeler)
 *   • frontend/src/features/export/sheets/perContractSheet.ts (ÖZET block)
 *   • frontend/src/pages/engagement/ContractDetailPage.tsx    (on-screen Özet)
 *
 * Single source of truth for the accrual window (MINI_FIX_CP) AND for how to
 * derive functional figures alongside native:
 *   • Window: `accrualMonthStart = row.date − 1 month` ∈ [audit start, audit end].
 *   • Functional values come from the schedule's `*Functional` columns when
 *     present (FX contracts); when absent (single-currency contracts where
 *     functional == contract ccy), native and functional are identical.
 *   • Initial recognition functional comes from `calc.fxSummary` directly.
 *   • Lifetime totals (Toplam Faiz/Ödeme/Amortisman) sum schedule `*Functional`
 *     columns across the full schedule.
 *   • Liability split functional: total = containing-row closingLiabilityFunctional;
 *     ST = Σ (paymentFunctional − interestExpenseFunctional) over the next 12
 *     periods after `containing`, clamped to total; LT = total − ST.
 *     This is the same formula `computeLiabilitySplit` uses on the backend but
 *     applied to the *Functional* schedule columns the engine already produced —
 *     it is reading existing data, not re-translating.
 *
 * All numeric values are returned as Decimal-precise strings (toFixed(2)) so
 * callers can render or accumulate without float drift. The shared helper does
 * NOT format for tr-TR or any locale — callers (Excel, screen) decide that.
 *
 * Functional-currency-agnostic. The engagement's `functionalCurrency` is the
 * reporting unit ("=1"); never hard-codes a specific ISO code.
 */

import Decimal from "decimal.js";

import type { CalculationResult, SchedulePeriod } from "@/types/result";

/**
 * One reporting figure, paired native + functional. Both are toFixed(2) strings.
 *
 * For a single-currency contract (functional == contract ccy), `native ===
 * functional` by construction (functional columns are absent and we fall back
 * to native — same number).
 */
export interface AuditFigure {
  native: string;
  functional: string;
}

/**
 * Complete per-contract audit-period figure set used by all three surfaces.
 */
export interface AuditFigures {
  /** İlk Yükümlülük (commencement) */
  initialLiability: AuditFigure;
  /** İlk ROU Varlığı (commencement) */
  initialROU: AuditFigure;
  /** Denetim Dönemi — Faiz (audit-period accrual window) */
  interest: AuditFigure;
  /** Denetim Dönemi — Amortisman (audit-period accrual window) */
  depreciation: AuditFigure;
  /** Denetim Dönemi — Kapanış Yükümlülüğü (containing-row closing or split total) */
  closingLiability: AuditFigure;
  /** Denetim Dönemi — Kapanış ROU (containing-row closing) */
  closingROU: AuditFigure;
  /** Vade Ayrımı — Kısa Vade */
  shortTerm: AuditFigure | undefined;
  /** Vade Ayrımı — Uzun Vade */
  longTerm: AuditFigure | undefined;
  /** Vade Ayrımı — Toplam */
  totalLiability: AuditFigure | undefined;
  /** Tüm Süre — Toplam Faiz */
  totalInterest: AuditFigure;
  /** Tüm Süre — Toplam Ödemeler */
  totalPayments: AuditFigure;
  /** Tüm Süre — Toplam Amortisman */
  totalDepreciation: AuditFigure;
}

function dec(v: string | number | undefined | null): Decimal {
  if (v === null || v === undefined || v === "") return new Decimal(0);
  try {
    const d = new Decimal(v as Decimal.Value);
    return d.isFinite() ? d : new Decimal(0);
  } catch {
    return new Decimal(0);
  }
}

/**
 * ISO date arithmetic: add `delta` months to a YYYY-MM-DD string, with
 * month-end CLAMPING (e.g. 2026-03-31 − 1 month → 2026-02-28, not 2026-03-03).
 * Returns the input verbatim on parse failure (defensive).
 *
 * Finding F2 — the previous body did `setUTCMonth(getUTCMonth() + delta)`
 * directly, which OVERFLOWS a month-end day into the following month when the
 * target month is shorter (Feb 31 → Mar 3). That mis-bucketed the accrual
 * window at audit-period boundaries (e.g. pulling a next-year row in). The
 * backend's engine clamps (dayjs `addMonths`) and stores the correct
 * `accrualMonthStart` on each row; this derivation must clamp identically.
 */
function addMonthsIso(iso: string, delta: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.getUTCDate();
  // Move to the 1st first so setUTCMonth cannot roll past the target month,
  // then clamp the day to the last valid day of the target month.
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + delta);
  const lastDayOfTargetMonth = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0),
  ).getUTCDate();
  d.setUTCDate(Math.min(day, lastDayOfTargetMonth));
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * Finding #13 / SPEC-09 — the "start-straddle" row for the audit period END.
 * row.date > auditPeriodEnd yet (row.date − 1 month) ∈ [start, end]. At most
 * one such row on the monthly grid. Mirrors the backend findStartStraddleRow
 * (SPEC-08) and the accrual window already used for interest/depreciation.
 */
function findStartStraddleRow(
  schedule: SchedulePeriod[],
  auditPeriodStart: string,
  auditPeriodEnd: string,
): SchedulePeriod | undefined {
  for (const row of schedule) {
    const accrualMonthStart = addMonthsIso(row.date, -1);
    if (
      row.date > auditPeriodEnd &&
      accrualMonthStart >= auditPeriodStart &&
      accrualMonthStart <= auditPeriodEnd
    ) {
      return row;
    }
  }
  return undefined;
}

/**
 * Pick the functional value when present, otherwise fall back to native.
 *
 * Single-currency contracts have undefined *Functional columns by design
 * (backend optimisation — they would be identical to native). Returning
 * native here makes the helper functional-currency-agnostic: callers always
 * get a meaningful value to display in the "functional" slot.
 */
function functionalOrNative(
  functional: string | undefined,
  native: string,
): string {
  return functional ?? native;
}

/**
 * Compute the paired (native + functional) audit-period figures for one
 * contract.
 *
 * @param perContract  The CalculationResult for the contract.
 * @param auditPeriodStart YYYY-MM-DD inclusive (audit FY start).
 * @param auditPeriodEnd   YYYY-MM-DD inclusive (audit FY end).
 */
export function computeAuditFigures(
  perContract: CalculationResult,
  auditPeriodStart: string,
  auditPeriodEnd: string,
): AuditFigures {
  const schedule: SchedulePeriod[] = perContract.schedule ?? [];

  // ── Accrual-window sums (MINI_FIX_CP / CO) ─────────────────────────────
  let interestN = new Decimal(0);
  let interestF = new Decimal(0);
  let depN = new Decimal(0);
  let depF = new Decimal(0);

  // ── Lifetime totals (Toplam Faiz/Ödeme/Amortisman) ─────────────────────
  let totalIntN = new Decimal(0);
  let totalIntF = new Decimal(0);
  let totalPayN = new Decimal(0);
  let totalPayF = new Decimal(0);
  let totalDepN = new Decimal(0);
  let totalDepF = new Decimal(0);

  // ── Containing period (last row with row.date <= auditPeriodEnd) ───────
  let containing: SchedulePeriod | undefined;
  let containingIdx = -1;

  for (let i = 0; i < schedule.length; i++) {
    const row = schedule[i]!;

    // Accrual-window inclusion: row.date − 1 month must fall inside the
    // audit window (mirrors the backend MINI_FIX_CO bucket key).
    const accrualMonthStart = addMonthsIso(row.date, -1);
    if (
      accrualMonthStart >= auditPeriodStart &&
      accrualMonthStart <= auditPeriodEnd
    ) {
      interestN = interestN.plus(dec(row.interestExpense));
      interestF = interestF.plus(
        dec(functionalOrNative(row.interestExpenseFunctional, row.interestExpense)),
      );
      depN = depN.plus(dec(row.depreciation));
      depF = depF.plus(
        dec(functionalOrNative(row.depreciationFunctional, row.depreciation)),
      );
    }

    // Lifetime totals (entire schedule).
    totalIntN = totalIntN.plus(dec(row.interestExpense));
    totalIntF = totalIntF.plus(
      dec(functionalOrNative(row.interestExpenseFunctional, row.interestExpense)),
    );
    totalPayN = totalPayN.plus(dec(row.payment));
    totalPayF = totalPayF.plus(
      dec(functionalOrNative(row.paymentFunctional, row.payment)),
    );
    totalDepN = totalDepN.plus(dec(row.depreciation));
    totalDepF = totalDepF.plus(
      dec(functionalOrNative(row.depreciationFunctional, row.depreciation)),
    );

    // Track the last row with row.date <= auditPeriodEnd ("containing row").
    if (row.date <= auditPeriodEnd) {
      containing = row;
      containingIdx = i;
    }
  }

  // ── Initial recognition — paired native + functional ───────────────────
  // For FX contracts the backend ships fxSummary.initialLeaseLiabilityFunctional
  // and initialROUAssetFunctional directly. For single-currency contracts
  // these are undefined → fall back to native (which equals functional).
  const initialLiability: AuditFigure = {
    native: new Decimal(perContract.initialLeaseLiability).toFixed(2),
    functional: new Decimal(
      perContract.fxSummary?.initialLeaseLiabilityFunctional ??
        perContract.initialLeaseLiability,
    ).toFixed(2),
  };
  const initialROU: AuditFigure = {
    native: new Decimal(perContract.initialROUAsset).toFixed(2),
    functional: new Decimal(
      perContract.fxSummary?.initialROUAssetFunctional ??
        perContract.initialROUAsset,
    ).toFixed(2),
  };

  // ── Closing balances — paired ──────────────────────────────────────────
  // Closing liability native: prefer the audit-end split.totalLiability, then
  // last in-window row, then year1ClosingLiability (legacy fallback).
  // Closing liability functional: same precedence but via *Functional columns
  // when available; falls back to native (single-currency) — identical.
  const lastRow = schedule[schedule.length - 1];
  const closingLiabNative =
    perContract.liabilitySplit?.totalLiability ??
    containing?.closingLiability ??
    lastRow?.closingLiability ??
    perContract.year1ClosingLiability;
  const closingLiabFunctional =
    containing?.closingLiabilityFunctional ??
    lastRow?.closingLiabilityFunctional ??
    closingLiabNative; // single-ccy fallback

  // Finding #13 / SPEC-09 — accrual-basis year-end ROU closing.
  //
  // The audit year's FINAL calendar month of depreciation (and any in-period
  // modification / index remeasurement that lands in the audit year's last
  // month) is carried by the "start-straddle" row — the row whose period-END
  // date sits in the next period (row.date > auditPeriodEnd) but whose accrual
  // month is still inside the audit window. Its `closingROU` is the ROU net
  // book value AT the reporting date on the accrual basis, exactly what the
  // authoritative ContractSummary.rouAssetClosing (functional, Balance-Sheet-
  // tied) reflects. Keeps the native ROU closing on the SAME accrual basis as
  // the native liability closing (liabilitySplit.totalLiability). Falls back to
  // the containing row when no straddle exists (lease ends at the reporting
  // date / single-row fixture) — identical to prior behaviour.
  const straddleRow = findStartStraddleRow(
    schedule,
    auditPeriodStart,
    auditPeriodEnd,
  );
  const closingROUNative =
    straddleRow?.closingROU ??
    containing?.closingROU ??
    lastRow?.closingROU ??
    perContract.year1ClosingROU;
  const closingROUFunctional =
    straddleRow?.closingROUFunctional ??
    containing?.closingROUFunctional ??
    lastRow?.closingROUFunctional ??
    closingROUNative;

  const closingLiability: AuditFigure = {
    native: new Decimal(closingLiabNative).toFixed(2),
    functional: new Decimal(closingLiabFunctional).toFixed(2),
  };
  const closingROU: AuditFigure = {
    native: new Decimal(closingROUNative).toFixed(2),
    functional: new Decimal(closingROUFunctional).toFixed(2),
  };

  // ── Liability split (ST / LT / Total) — paired ─────────────────────────
  // Native side comes from perContract.liabilitySplit (backend authoritative).
  // Functional side derives ST/LT/Total from existing *Functional columns:
  //   functionalTotal = containing.closingLiabilityFunctional
  //   functionalST    = Σ (paymentFunctional − interestExpenseFunctional)
  //                     over containingIdx+1 .. containingIdx+12 (clamped to total)
  //   functionalLT    = functionalTotal − functionalST
  // This is the same formula the backend's computeLiabilitySplit uses,
  // applied to the schedule's already-translated *Functional columns —
  // no re-translation. Skipped entirely when liabilitySplit is absent
  // (terminated contracts; backend suppresses split → we surface "—").
  let shortTerm: AuditFigure | undefined;
  let longTerm: AuditFigure | undefined;
  let totalLiability: AuditFigure | undefined;
  if (perContract.liabilitySplit && containingIdx >= 0) {
    const ls = perContract.liabilitySplit;
    let stF = new Decimal(0);
    const endIdx = Math.min(schedule.length - 1, containingIdx + 12);
    for (let j = containingIdx + 1; j <= endIdx; j++) {
      const r = schedule[j]!;
      const payF = dec(functionalOrNative(r.paymentFunctional, r.payment));
      const intF = dec(
        functionalOrNative(r.interestExpenseFunctional, r.interestExpense),
      );
      stF = stF.plus(payF.minus(intF));
    }
    const totalF = new Decimal(closingLiabFunctional);
    const stClampedF = stF.gt(totalF) ? totalF : stF;
    const ltF = totalF.minus(stClampedF);

    shortTerm = {
      native: new Decimal(ls.shortTerm).toFixed(2),
      functional: stClampedF.toFixed(2),
    };
    longTerm = {
      native: new Decimal(ls.longTerm).toFixed(2),
      functional: ltF.toFixed(2),
    };
    totalLiability = {
      native: new Decimal(ls.totalLiability).toFixed(2),
      functional: totalF.toFixed(2),
    };
  }

  return {
    initialLiability,
    initialROU,
    interest: { native: interestN.toFixed(2), functional: interestF.toFixed(2) },
    depreciation: { native: depN.toFixed(2), functional: depF.toFixed(2) },
    closingLiability,
    closingROU,
    shortTerm,
    longTerm,
    totalLiability,
    totalInterest: {
      native: totalIntN.toFixed(2),
      functional: totalIntF.toFixed(2),
    },
    totalPayments: {
      native: totalPayN.toFixed(2),
      functional: totalPayF.toFixed(2),
    },
    totalDepreciation: {
      native: totalDepN.toFixed(2),
      functional: totalDepF.toFixed(2),
    },
  };
}
